/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useCallback } from 'react';

import { Form, Formik } from 'formik';

import { TbBriefcase, TbCalendar, TbClock, TbMapPin } from 'react-icons/tb';

import { twMerge } from 'tailwind-merge';

import Typography from 'components-new/typography/typography';
import Divider from 'components-new/divider/divider';
import Icon from 'components-new/icon/icon';
import Button from 'components-new/button/button';
import type { SelectOption } from 'components-new/select-input/select-input';

import schema from 'pages/organization/OrganisationSettingsPage/integration-settings/create-jobs-page/create-sdb-job-schema';

import type { UseOrganisationSettingsIntegrationCreateHandler } from 'state/remote/remote-organization/organisation-settings/use-organisation-settings-integration-create';
import type { GenericImportedJobType } from 'state/remote/remote-organization/organisation-settings/use-organisation-settings-integration-select';

import TextField from 'form-elements/text-field';
import SelectField from 'form-elements/select-field';
import { createLocale } from 'locales';
import type { RateRule } from 'state/__generated__/tadaahAPI';

const localize = createLocale('organisationIntegrationSettingsLocales');

interface FetchedJobFormCardProps {
  job: GenericImportedJobType;
  formOptions: UseOrganisationSettingsIntegrationCreateHandler['formOptions'];
  form: UseOrganisationSettingsIntegrationCreateHandler['form'];
  rateRules: RateRule[] | undefined;
  isCreated: boolean;
}

const FetchedJobFormCard: React.FC<FetchedJobFormCardProps> = ({
  job,
  formOptions,
  form,
  rateRules,
  isCreated,
}) => {
  const determineRate = useCallback(
    (competence: string | undefined, field: 'max_rate' | 'max_rate_exempt') => {
      const unmatchedRateRule = 30;
      const rateRule = rateRules?.find(
        (rule) => rule.competence === competence,
      );
      if (!rateRule) return unmatchedRateRule;
      if (field === 'max_rate') return rateRule.rate;
      if (field === 'max_rate_exempt') return rateRule.rate_exempt;
      return unmatchedRateRule;
    },
    [rateRules],
  );

  const onCreateJobButtonClick = useCallback(
    (
      setFieldValue: (
        field: string,
        value: any,
        shouldValidate?: boolean | undefined,
      ) => void,
    ) => {
      setFieldValue('sdb_job_id', job.id);
      setFieldValue('date', job.date);
      setFieldValue('start', job.start);
      setFieldValue('end', job.end);
    },
    [job],
  );

  const onSelectedCompetenceChange = useCallback(
    (
      value: SelectOption | undefined,
      setFieldValue: (
        field: string,
        value: any,
        shouldValidate?: boolean | undefined,
      ) => void,
    ) => {
      setFieldValue('max_rate', determineRate(value?.value, 'max_rate'));
      setFieldValue(
        'max_rate_exempt',
        determineRate(value?.value, 'max_rate_exempt'),
      );
      setFieldValue('competence', value?.value);
    },
    [determineRate],
  );

  const jobCardContainerClassName = twMerge(
    'my-2 p-5 border rounded-lg',
    isCreated ? 'bg-gray-100' : 'bg-white',
  );

  const initialValues = {
    ...form.initialValues,
    date: job.date,
    end: job.end,
    max_rate: determineRate(undefined, 'max_rate'),
    max_rate_exempt: determineRate(undefined, 'max_rate_exempt'),
    pause: job.pause,
    sdb_job_id: job.id,
    start: job.start,
  };

  return (
    <div className={jobCardContainerClassName}>
      <div className="flex flex-col gap-2">
        <div className="flex flex-col gap-2 mb-2">
          <div className="flex flex-col gap-2 mb-2">
            <Typography variant="body-large-bold" as="span">
              {job.title}
            </Typography>
          </div>
          <div className="flex flex-col gap-2 text-gray-400">
            <div className="flex flex-row gap-2 items-center">
              <Icon icon={TbCalendar} />
              <Typography variant="body-large" as="span">
                {job.date}
              </Typography>
            </div>
            <div className="flex flex-row gap-2 items-center lowercase">
              <Icon icon={TbClock} />
              <Typography variant="body-large" as="span">
                {job.start} {localize('to')} {job.end}
              </Typography>
            </div>
            <div className="flex flex-row gap-2 items-center">
              <Icon icon={TbBriefcase} />
              <Typography variant="body-large" as="span">
                {job.function}
              </Typography>
            </div>
            <div className="flex flex-row gap-2 items-center">
              <Icon icon={TbMapPin} />
              <Typography variant="body-large" as="span">
                {job.location}
              </Typography>
            </div>
          </div>
        </div>

        <Divider orientation="horizontal" />

        <Formik
          initialValues={initialValues}
          onSubmit={form.onSubmit}
          validationSchema={schema}
        >
          {({ setFieldValue }) => (
            <Form>
              <div className="flex flex-col gap-3 my-3">
                <SelectField
                  name="location"
                  title={localize('location')}
                  placeholder={localize('selectLocation')}
                  options={formOptions.locationOptions}
                  disabled={isCreated}
                />
                <SelectField
                  name="competence"
                  title={localize('competence')}
                  placeholder={localize('selectCompetence')}
                  disabled={isCreated}
                  options={formOptions.competenceOptions}
                  onSelectedOptionsChange={(value) =>
                    onSelectedCompetenceChange(
                      value as SelectOption,
                      setFieldValue,
                    )
                  }
                />
                <SelectField
                  multiple
                  name="certificates"
                  title={localize('certificates')}
                  placeholder={localize('selectCertificatesMultiple')}
                  disabled={isCreated}
                  options={formOptions.certificateOptions}
                />
                <TextField
                  name="pause"
                  title={localize('break')}
                  disabled={isCreated}
                  placeholder={localize('selectBreakTime')}
                />
                <TextField
                  name="max_rate"
                  title={localize('maxRate')}
                  disabled={isCreated}
                  placeholder={localize('selectMaxRate')}
                />
                <TextField
                  name="max_rate_exempt"
                  disabled={isCreated}
                  title={localize('maxRateExempt')}
                />
              </div>
              <Button
                label={
                  isCreated ? localize('jobCreated') : localize('createJob')
                }
                variant={isCreated ? 'secondary' : 'primary'}
                className="ml-auto"
                type="submit"
                disabled={form.isLoading || isCreated}
                onClick={() => onCreateJobButtonClick(setFieldValue)}
              />
            </Form>
          )}
        </Formik>
      </div>
    </div>
  );
};

export default FetchedJobFormCard;
